import { buildAccommodationOfferPageRegenerationScopeKey } from 'tripPlanner/assistant/surfacePolicy'
import type { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { take } from 'lib/array/arrayUtils'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import {
  displayContextField,
  nonEmptyString,
} from './offerPageContextFormatting'
import { buildOfferPageFinePrintSections } from './offerPageFinePrintContext'
import { buildRoomPackageSummaries } from './offerPageRoomPackageContext'
import type {
  AccommodationOfferPageAssistantAirportContextRequest,
  AccommodationOfferPageAssistantDisplayContextRequest,
  AccommodationOfferPageAssistantOccupancyContextRequest,
  AccommodationOfferPageAssistantPageIdentityRequest,
  AccommodationOfferPageAssistantSelectedDatesContextRequest,
  AccommodationOfferPageAssistantSurfaceContextRequest,
  BuildOfferPageSurfaceContextProps,
} from './offerPageContextTypes'

const FINE_PRINT_SECTION_LIMIT = 8
const SIMILAR_OFFERS_LIMIT = 5

export type { PropertyRoomRateViewForAssistant } from './offerPageContextTypes'
export { formatOfferPageLoyaltyEarnSummary } from './offerPageContextFormatting'
export { buildOfferPageSimilarOffers, makeGetOfferPageSimilarOffers } from './offerPageSimilarOffers'

function getAccommodationOfferPageIdentity(
  offerPageState: OfferPageState,
): AccommodationOfferPageAssistantPageIdentityRequest | undefined {
  const propertyPageId = nonEmptyString(offerPageState.property?.id)
  if (propertyPageId) {
    return {
      propertyId: propertyPageId,
    }
  }

  if (isLEHotel(offerPageState.offer)) {
    const offerId = nonEmptyString(offerPageState.offer.id)
    if (offerId) {
      const propertyId = nonEmptyString(offerPageState.offer.property?.id)

      return {
        offerId,
        ...(propertyId ? { propertyId } : {}),
      }
    }
  }

  const bedbankOfferId = nonEmptyString(offerPageState.bedbankOffer?.id)
  if (bedbankOfferId) {
    return {
      offerId: bedbankOfferId,
    }
  }

  return undefined
}

export function buildOfferPageRegenerationScopeKey(offerPageState: OfferPageState): string | undefined {
  const identity = getAccommodationOfferPageIdentity(offerPageState)
  return identity ? buildAccommodationOfferPageRegenerationScopeKey(identity) : undefined
}

export function canBuildOfferPageSurfaceContext(offerPageState: OfferPageState): boolean {
  return getAccommodationOfferPageIdentity(offerPageState) !== undefined
}

function buildSelectedDates(offerPageState: OfferPageState): AccommodationOfferPageAssistantSelectedDatesContextRequest | undefined {
  if (!offerPageState.checkInDate && !offerPageState.checkOutDate && !offerPageState.duration) {
    return undefined
  }

  return {
    ...(offerPageState.checkInDate ? { checkIn: offerPageState.checkInDate } : {}),
    ...(offerPageState.checkOutDate ? { checkOut: offerPageState.checkOutDate } : {}),
    ...(offerPageState.duration ? { durationNights: offerPageState.duration } : {}),
  }
}

function buildOccupancy(rooms: Array<OfferPageRoom>): AccommodationOfferPageAssistantOccupancyContextRequest | undefined {
  if (rooms.length === 0) {
    return undefined
  }

  const contextRooms = rooms.map(room => ({
    adults: room.adults,
    ...(room.children !== undefined ? { children: room.children } : {}),
    ...(room.teenagers !== undefined ? { teenagers: room.teenagers } : {}),
    ...(room.infants !== undefined ? { infants: room.infants } : {}),
    ...(room.childrenAge?.length ? { childrenAge: room.childrenAge } : {}),
  }))

  return {
    rooms: contextRooms,
    totalAdults: rooms.reduce((sum, room) => sum + room.adults, 0),
    totalChildren: rooms.reduce((sum, room) => sum + (room.children ?? 0) + (room.teenagers ?? 0), 0),
    totalInfants: rooms.reduce((sum, room) => sum + (room.infants ?? 0), 0),
  }
}

function buildAirport(offerPageState: OfferPageState): AccommodationOfferPageAssistantAirportContextRequest | undefined {
  if (!offerPageState.airport?.code) {
    return undefined
  }

  return {
    code: offerPageState.airport.code,
  }
}

function getDisplayContextIdentity(
  offerPageState: OfferPageState,
): Pick<AccommodationOfferPageAssistantDisplayContextRequest, 'offerName' | 'propertyName' | 'tagline' | 'destination'> {
  if (offerPageState.property) {
    return {
      propertyName: displayContextField(offerPageState.property.name),
      tagline: displayContextField(offerPageState.property.headline),
      destination: displayContextField(offerPageState.property.location.title) ?? displayContextField(offerPageState.property.location.formattedAddress),
    }
  }

  if (isLEHotel(offerPageState.offer)) {
    return {
      offerName: displayContextField(offerPageState.offer.name),
      propertyName: displayContextField(offerPageState.offer.property?.name),
      tagline: displayContextField(offerPageState.offer.description),
      destination: displayContextField(offerPageState.offer.location),
    }
  }

  if (isBedbankOffer(offerPageState.bedbankOffer)) {
    return {
      offerName: displayContextField(offerPageState.bedbankOffer.name),
      propertyName: displayContextField(offerPageState.bedbankOffer.property.name),
      tagline: displayContextField(offerPageState.bedbankOffer.tagline),
      destination: displayContextField(offerPageState.bedbankOffer.property.location),
    }
  }

  return {}
}

function buildDisplayContext({
  offerPageState,
  priceFormatContext,
  hotelPackageViews,
  bedbankPackageViews,
  propertyRoomRateViews,
  roomPackageLoyaltyEarnTotals,
  similarOffers,
}: Pick<BuildOfferPageSurfaceContextProps, 'offerPageState' | 'priceFormatContext' | 'hotelPackageViews' | 'bedbankPackageViews' | 'propertyRoomRateViews' | 'roomPackageLoyaltyEarnTotals' | 'similarOffers'>): AccommodationOfferPageAssistantDisplayContextRequest | undefined {
  const identity = getDisplayContextIdentity(offerPageState)
  const finePrintSections = take(
    buildOfferPageFinePrintSections(offerPageState),
    FINE_PRINT_SECTION_LIMIT,
  )
  const roomPackageSummaries = buildRoomPackageSummaries({
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    roomPackageLoyaltyEarnTotals,
    priceFormatContext,
  })
  const limitedSimilarOffers = take(similarOffers ?? [], SIMILAR_OFFERS_LIMIT)

  const displayContext: AccommodationOfferPageAssistantDisplayContextRequest = {
    ...identity,
    ...(finePrintSections.length ? { finePrintSections } : {}),
    ...(roomPackageSummaries.length ? { roomPackageSummaries } : {}),
    ...(limitedSimilarOffers.length ? { similarOffers: limitedSimilarOffers } : {}),
  }

  return Object.keys(displayContext).length ? displayContext : undefined
}

export function buildOfferPageSurfaceContext({
  offerPageState,
  pageUrl,
  priceFormatContext,
  hotelPackageViews,
  bedbankPackageViews,
  propertyRoomRateViews,
  roomPackageLoyaltyEarnTotals,
  similarOffers,
}: BuildOfferPageSurfaceContextProps): AccommodationOfferPageAssistantSurfaceContextRequest {
  const pageIdentity = getAccommodationOfferPageIdentity(offerPageState)
  if (!pageIdentity) {
    throw new Error('Accommodation offer-page assistant requires an offer or property id')
  }

  const selectedDates = buildSelectedDates(offerPageState)
  const occupancy = buildOccupancy(offerPageState.rooms)
  const airport = buildAirport(offerPageState)
  const displayContext = buildDisplayContext({
    offerPageState,
    priceFormatContext,
    hotelPackageViews,
    bedbankPackageViews,
    propertyRoomRateViews,
    roomPackageLoyaltyEarnTotals,
    similarOffers,
  })

  return {
    type: 'accommodation_offer_page',
    schemaVersion: 1,
    pageIdentity,
    url: pageUrl,
    ...(selectedDates ? { selectedDates } : {}),
    ...(occupancy ? { occupancy } : {}),
    ...(airport ? { airport } : {}),
    ...(displayContext ? { displayContext } : {}),
  }
}
